import { useEffect, useState } from "react";
import { X, Maximize2 } from "lucide-react";
import { publicApi } from "@/lib/api";
import { trackGalleryClick, trackProductClick } from "@/lib/analytics";
import ShareButton from "@/components/ShareButton";

const CATEGORY_LABEL = {
  painting: "Painting",
  decoration: "Decoration",
  sketch: "Sketch",
  other: "Studio work",
};

const siteOrigin = () => (typeof window !== "undefined" ? window.location.origin : "https://camoriginal.com");

export default function Portfolio() {
  const [prints, setPrints] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    Promise.all([publicApi.listProducts({ category: "prints" }), publicApi.listGallery()])
      .then(([p, g]) => {
        setPrints(p);
        setGallery(g);
      })
      .finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="page-portfolio" className="max-w-[1400px] mx-auto px-5 md:px-10 py-16 text-white">
      <p className="overline mb-3">Portfolio</p>
      <h1 className="font-display text-5xl md:text-7xl mb-4">Selected Work</h1>
      <p className="text-white/60 max-w-2xl mb-16">
        A cross-section of fine art prints and reproductions from the CKA collection. Every piece carries a story from Country. Original canvases will be added as they become available for sale.
      </p>

      {/* FINE ART PRINTS (available for sale) */}
      <section className="mb-24">
        <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
          <div>
            <p className="overline mb-2">Available as prints</p>
            <h2 className="font-display text-3xl md:text-5xl">Fine Art Prints</h2>
          </div>
          <p className="text-xs uppercase tracking-widest text-white/40">{prints.length} pieces</p>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="aspect-[4/3] bg-white/5 border border-white/10 animate-pulse" />
            ))}
          </div>
        ) : prints.length === 0 ? (
          <p data-testid="portfolio-empty" className="text-white/50 py-24 text-center">No pieces yet.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {prints.map((p, i) => (
              <article
                key={p.id}
                data-testid={`portfolio-tile-${i}`}
                className="group border border-white/10 bg-[#141414] overflow-hidden relative"
              >
                <button
                  type="button"
                  onClick={() => { trackProductClick(p.id, "portfolio"); setLightbox({ img: p.image_url, title: p.title, subtitle: "Fine art print", note: p.description }); }}
                  className="block w-full aspect-[4/3] overflow-hidden relative cursor-zoom-in"
                >
                  <img src={p.image_url} alt={p.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  <span className="absolute top-3 left-3 bg-[#0A0A0A]/70 backdrop-blur border border-white/20 text-white/90 text-[10px] uppercase tracking-widest px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity inline-flex items-center gap-1">
                    <Maximize2 className="w-3 h-3" /> Click to enlarge
                  </span>
                </button>
                <div className="absolute top-3 right-3 z-10">
                  <ShareButton
                    testid={`portfolio-tile-share-${i}`}
                    iconOnly
                    title={`${p.title} — Fine art print · CamOriginal`}
                    text={p.description ? p.description.slice(0, 140) : "Signed fine art print by Cameron Boyle."}
                    url={`${siteOrigin()}/shop/${p.id}`}
                    label={`Share ${p.title}`}
                  />
                </div>
                <div className="p-6 md:p-8">
                  <p className="overline mb-2">Fine Art Print</p>
                  <h3 className="font-display text-2xl md:text-3xl tracking-wider mb-3">{p.title}</h3>
                  {p.description && (
                    <p className="text-white/70 leading-relaxed text-sm whitespace-pre-line">{p.description}</p>
                  )}
                </div>
              </article>
            ))}
          </div>
        )}
      </section>

      {/* STUDIO GALLERY — not for sale, just showing work */}
      <section id="studio-gallery" className="border-t border-white/10 pt-16">
        <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
          <div>
            <p className="overline mb-2">The studio archive</p>
            <h2 className="font-display text-3xl md:text-5xl">Studio Gallery</h2>
            <p className="text-white/60 mt-3 max-w-2xl text-sm md:text-base">
              A rolling wall of everything Cam&rsquo;s made — paintings, hand-decorated pieces, sketches, one-offs. Not for sale, just here to be seen.
            </p>
          </div>
          <p className="text-xs uppercase tracking-widest text-white/40">{gallery.length} pieces</p>
        </div>

        {gallery.length === 0 ? (
          <div data-testid="studio-gallery-empty" className="border border-dashed border-white/10 py-20 px-6 text-center bg-[#0F0F0F]">
            <p className="font-display text-2xl mb-2">The archive is warming up.</p>
            <p className="text-white/50 text-sm max-w-md mx-auto">Cam will drop new work in here as it&rsquo;s finished. Check back soon.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
            {gallery.map((g, i) => (
              <div
                key={g.id}
                data-testid={`studio-gallery-tile-${i}`}
                className="group relative aspect-square bg-[#141414] border border-white/10 overflow-hidden hover:border-[#FF4500] transition-colors"
              >
                <button
                  type="button"
                  onClick={() => { trackGalleryClick(g.id); setLightbox({ img: g.image_url, title: g.title, subtitle: CATEGORY_LABEL[g.category] || "Studio work", note: g.description }); }}
                  className="block w-full h-full text-left cursor-zoom-in"
                  aria-label={`View ${g.title}`}
                >
                  <img src={g.image_url} alt={g.title} loading="lazy" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  <div className="absolute inset-x-0 bottom-0 p-3 md:p-4 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/85 to-transparent pt-12">
                    <p className="text-[10px] uppercase tracking-widest text-[#FF4500] font-semibold mb-0.5">{CATEGORY_LABEL[g.category] || "Studio work"}</p>
                    <p className="font-display text-sm md:text-base tracking-wider leading-tight truncate">{g.title}</p>
                  </div>
                </button>
                <div className="absolute top-2 right-2 z-10 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
                  <ShareButton
                    testid={`studio-gallery-share-${i}`}
                    iconOnly
                    title={`${g.title} — Studio work · CamOriginal`}
                    text={g.description ? g.description.slice(0, 140) : `${CATEGORY_LABEL[g.category] || "Studio work"} by Cameron Boyle.`}
                    url={`${siteOrigin()}/portfolio#studio-${g.id}`}
                    label={`Share ${g.title}`}
                    className="!w-8 !h-8"
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* LIGHTBOX */}
      {lightbox && (
        <div
          data-testid="portfolio-lightbox"
          className="fixed inset-0 z-[100] bg-[#0A0A0A]/95 backdrop-blur-sm flex items-center justify-center p-4 md:p-10 animate-in fade-in duration-200"
          onClick={() => setLightbox(null)}
        >
          <button
            data-testid="portfolio-lightbox-close"
            onClick={() => setLightbox(null)}
            className="absolute top-4 right-4 md:top-8 md:right-8 w-11 h-11 border border-white/20 bg-[#0A0A0A]/60 flex items-center justify-center hover:border-[#FF4500] hover:text-[#FF4500] z-10"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
          <div className="relative max-w-[92vw] max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <img src={lightbox.img} alt={lightbox.title} className="max-w-full max-h-[78vh] object-contain mx-auto" />
            <div className="mt-5 text-white text-center md:text-left">
              <p className="text-[10px] uppercase tracking-widest text-white/40 mb-1">{lightbox.subtitle}</p>
              <p className="font-display text-2xl md:text-3xl tracking-wider">{lightbox.title}</p>
              {lightbox.note && <p className="text-sm text-white/70 mt-2 max-w-2xl">{lightbox.note}</p>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
